/** @jsxImportSource ./simboluri-jsx */
import { Ductilitate as DuctilitateT, StepState, StrategieResult, fmtDate } from "./tipuri";
import Info from "./Info";
import RegimuriGhid from "./RegimuriGhid";
import { DescriereaPasului } from "./TitluPas";
import Marime, { EtichetaNatura } from "./Marime";

import DeciziiPas from "./DeciziiPas";
import ProeminentaCalcul from "./ProeminentaCalcul";
import NotePas from "./NotePas";
import { t, useT } from "../i18n";
import type { Cheie } from "../i18n";
// Pasul 3 — Strategia structurală: patru „bugete" de beton (ρ) calculate
// pe aceleași intrări; cel mai mare dictează strategia structurii. Fiecare
// intrare e afișată cu sursa ei (Pasul 1 / Pasul 2 / metodă / presupunere).

const RHO_LABELS: Record<string, Cheie> = {
  gravitational: "struct.strateg.rho_gravitational",
  forfecare: "struct.strateg.rho_forfecare",
  drift: "struct.strateg.rho_drift",
  constructiv: "struct.strateg.rho_constructiv",
};

// Explicația și formula fiecărui buget — afișate la ⓘ pe rândul lui. E o
// funcție, nu o constantă: textele se cer la randare, în limba de atunci.
const rhoInfo = (): Record<string, { titlu: string; ce: string; deCe: string; parametri: { simbol: string; nume: string; sursa: string }[] }> => ({
  gravitational: {
    titlu: t("struct.strateg.bugetul_gravitational"),
    ce: t("struct.strateg.beton_iti_trebuie"),
    deCe: t("struct.strateg.stalp_aduna_greutatea"),
    parametri: [
      { simbol: "n", nume: t("struct.strateg.numarul_niveluri_deasupr"), sursa: t("struct.strateg.sursa_pasul_2") },
      { simbol: "g_grav", nume: t("struct.strateg.incarcarea_gravitational"), sursa: t("struct.strateg.constanta_a_metodei") },
      { simbol: "νt", nume: t("struct.strateg.apasarea_maxima_admisa"), sursa: t("struct.strateg.p100_1_presupunere") },
      { simbol: "f_cd", nume: t("struct.strateg.rezistenta_calcul_a"), sursa: t("struct.strateg.clasa_beton_date") },
    ],
  },
  forfecare: {
    titlu: t("struct.strateg.bugetul_forfecare"),
    ce: t("struct.strateg.beton_iti_trebuie2"),
    deCe: t("struct.strateg.cutremurul_impinge_cladi"),
    parametri: [
      { simbol: "V", nume: t("struct.strateg.forta_taietoare_baza"), sursa: t("struct.strateg.calculata_jos_v") },
      { simbol: "A", nume: t("struct.strateg.aria_unui_nivel"), sursa: t("struct.strateg.sursa_pasul_2_media_niv") },
      { simbol: "v_Rd", nume: t("struct.strateg.tinta_practica_forfecare"), sursa: t("struct.strateg.documentul_metoda") },
    ],
  },
  drift: {
    titlu: t("struct.strateg.bugetul_drift_indoire"),
    ce: t("struct.strateg.beton_iti_trebuie3"),
    deCe: t("struct.strateg.cladire_inalta_lucreaza"),
    parametri: [
      { simbol: "I_req", nume: t("struct.strateg.inertia_totala_necesara"), sursa: t("struct.strateg.calculata_jos_v2") },
      { simbol: "l_typ", nume: t("struct.strateg.lungimea_tipica_element"), sursa: t("struct.strateg.masurata_peretii_persist") },
      { simbol: "A", nume: t("struct.strateg.aria_unui_nivel"), sursa: t("struct.strateg.sursa_pasul_2") },
    ],
  },
  constructiv: {
    titlu: t("struct.strateg.bugetul_constructiv"),
    ce: t("struct.strateg.beton_iese_sine"),
    deCe: t("struct.strateg.chiar_daca_seismic"),
    parametri: [
      { simbol: "n_elem", nume: t("struct.strateg.numarul_elemente_estimat"), sursa: t("struct.strateg.calculat_jos") },
      { simbol: "A_elem", nume: t("struct.strateg.aria_unui_element"), sursa: t("struct.strateg.regula_alcatuire_armatur") },
      { simbol: "0,5", nume: t("struct.strateg.doar_circa_jumatate"), sursa: t("struct.strateg.documentul_metoda") },
    ],
  },
});

export default function StepStrategie({
  state,
  running,
}: {
  state: StepState | null;
  running: boolean;
}) {
  useT();
  if (running) {
    return (
      <div className="st-status">
        <div className="st-spinner" />
        <div>
          <strong>{t("struct.strateg.pasul_ruleaza")}</strong>
          <div className="sg-muted">
            {t("struct.strateg.masoara_l_typ2")}
          </div>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="sg-muted st-empty">
        {t("struct.strateg.nerulat")}
        {/* descrierea vine din catalog_pasi.py — același text cu
            cel de la hover pe titlul pasului */}
        <DescriereaPasului cheie="strategie_arhitecturala" />
      </div>
    );
  }

  const r = state.result as unknown as StrategieResult;

  if (state.status === "error") {
    return (
      <div>
        <div className="sg-error">
          {t("struct.pas.esuat", { eroare: r?.error ?? t("struct.pas.eroare_necunoscuta") })}
        </div>
        <div className="sg-muted">{t("struct.strateg.ruleaza_nou_meniul")}</div>
      </div>
    );
  }

  const rhoMax = Math.max(...Object.values(r.rho));
  const RHO_INFO = rhoInfo();

  return (
    <div className="st-result">
      <div className="st-cards">
        <div className="st-card st-card-main">
          <div className="st-card-label">
            {t("struct.strateg.constrangerea_guverneaza")}
            <Info
              titlu={t("struct.strateg.decide")}
              ce={t("struct.strateg.constrangere_cere_anumit")}
              deCe={t("struct.strateg.strategia_structurii_cad")}
              parametri={[
                { simbol: "ρ", nume: t("struct.strateg.aria_beton_aria"), sursa: t("struct.strateg.formulele_a_d") },
              ]}
            />
          </div>
          <div className="st-card-value">{r.strategie.nume}</div>
          <div className="st-card-sub">{r.strategie.descriere}</div>
        </div>
        <div className="st-card st-card-main">
          <div className="st-card-label">{t("struct.strateg.structura_recomandata")}<Marime cheie="structura_recomandata" /></div>
          <div className="st-card-value" style={{ fontSize: 22 }}>
            {r.strategie.structura}
          </div>
          <div className="st-card-sub">{r.strategie.optimizare}</div>
        </div>
      </div>

      {/* deciziile pasului stau ÎNAINTEA criteriilor pe care le-au produs:
          «s-a selectat DCH deși economic ar fi DCM» se citește împreună cu
          tabelul de criterii de mai jos, nu într-o pagină separată */}
      <DeciziiPas decizii={r.decizii} titlu="struct.decizii.pas3" />
      <NotePas note={r.avertismente} titlu="struct.note.titlu" />

      {r.ductilitate && <Ductilitate d={r.ductilitate} />}

      {/* etajul retras: încadrarea la 20 % pe secțiuni și structura, separat */}
      <ProeminentaCalcul p={r.proeminenta} />

      <div className="st-findings">
        <h3>{t("struct.strateg.cele_patru_bugete")}</h3>
        <table className="sg-table">
          <thead>
            <tr><th>{t("struct.strateg.constrangere")}</th><th>ρ</th><th></th></tr>
          </thead>
          <tbody>
            {Object.entries(r.rho)
              .sort((a, b) => b[1] - a[1])
              .map(([k, v]) => (
                <tr key={k} style={k === r.guverneaza ? { fontWeight: 600 } : {}}>
                  <td>
                    {RHO_LABELS[k] ? t(RHO_LABELS[k]) : k}
                    {RHO_INFO[k] && (
                      <Info
                        titlu={RHO_INFO[k].titlu}
                        ce={RHO_INFO[k].ce}
                        deCe={RHO_INFO[k].deCe}
                        parametri={RHO_INFO[k].parametri}
                      />
                    )}
                    {k === r.guverneaza ? t("struct.strateg.sageata_guverneaza") : ""}
                    {/* la pereți, bugetul gravitațional e jumătatea pe
                        direcție a celui total — ceilalți trei sunt pe direcție */}
                    {k === "gravitational" && r.intermediare.nota_rho_grav && (
                      <div className="sg-muted" style={{ fontSize: 11 }}>
                        {r.intermediare.nota_rho_grav}
                        {r.intermediare.rho_grav_total != null
                          && ` (total ${(r.intermediare.rho_grav_total * 100).toFixed(2)}%)`}
                      </div>
                    )}
                  </td>
                  <td className="sg-mono">{(v * 100).toFixed(2)}%</td>
                  <td style={{ width: "40%" }}>
                    <div style={{
                      background: k === r.guverneaza ? "#b5323d" : "#b5c9b7",
                      height: 10,
                      width: `${Math.max(2, (v / rhoMax) * 100)}%`,
                      borderRadius: 3,
                    }} />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="st-findings">
        <h3>{t("struct.strateg.intrarile_sursa_fiecarei")}</h3>
        <table className="sg-table">
          <thead>
            <tr><th>{t("struct.caz.col_simbol")}</th><th>{t("struct.rzv.col_valoare")}</th><th>{t("struct.gamma.col_sursa")}</th></tr>
          </thead>
          <tbody>
            {r.intrari.map((x, i) => (
              <tr key={i}>
                <td className="sg-mono"><strong>{x.simbol}</strong></td>
                <td className="sg-mono">
                  {x.valoare} {x.unitate !== "-" ? x.unitate : ""}
                </td>
                <td className="sg-muted">{x.sursa}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {r.l_typ_directii && r.l_typ_directii.length > 0 && (
        <div className="st-findings">
          <h3>
            {t("struct.strateg.l_typ_directii")}
            <Info
              titlu={t("struct.strateg.masoara_l_typ")}
              ce={t("struct.strateg.iau_doar_peretii")}
              deCe={t("struct.strateg.alocarea_elementelor_str")}
              parametri={[
                { simbol: "≥2 m", nume: t("struct.strateg.lungimea_minima_a"), sursa: t("struct.strateg.regula_practica") },
                { simbol: "top 6", nume: t("struct.strateg.cate_tronsoane_intra"), sursa: t("struct.strateg.documentul_metoda_n") },
              ]}
            />
          </h3>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.strateg.directie")}</th>
                <th>{t("struct.strateg.tronsoane")}</th>
                <th>{t("struct.strateg.cele_lungi")}</th>
                <th>l_typ</th>
              </tr>
            </thead>
            <tbody>
              {r.l_typ_directii.map((d, i) => (
                <tr key={i}>
                  <td className="sg-mono">{d.unghi_deg}°</td>
                  <td className="sg-mono">{d.nr_tronsoane}</td>
                  <td className="sg-mono">
                    {d.top.map((t) => `${t.lungime_m} m (${t.perete})`).join(", ")}
                  </td>
                  <td className="sg-mono"><strong>{d.l_typ_m} m</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="st-findings">
        <h3>{t("struct.strateg.valori_intermediare")}</h3>
        <table className="sg-table">
          <tbody>
            <tr>
              <td>
                {t("struct.strateg.rand_h")}
                <Info
                  titlu={t("struct.strateg.h_inaltimea_totala")}
                  ce={t("struct.strateg.formula_h_n")}
                  deCe={t("struct.strateg.h_intra_puterea")}
                  parametri={[
                    { simbol: "n", nume: t("struct.strateg.numarul_niveluri_deasupr"), sursa: t("struct.strateg.sursa_pasul_2") },
                    { simbol: "h_et", nume: t("struct.strateg.inaltimea_medie_etaj"), sursa: t("struct.strateg.pasul_2_cotele") },
                  ]}
                />
              </td>
              <td className="sg-mono">{r.intermediare.H_m} m</td>
            </tr>
            <tr>
              <td>
                {t("struct.strateg.rand_lambda")}
                <Info
                  titlu={t("struct.strateg.corectia_modul_vibratie")}
                  ce={t("struct.strateg.0_85_cladiri")}
                  deCe={t("struct.strateg.cladirile_inalte_toata")}
                  parametri={[{ simbol: "λ", nume: t("struct.strateg.0_85_daca"), sursa: t("struct.strateg.p100_1_metoda") }]}
                />
              </td>
              <td className="sg-mono">{r.intermediare.lambda}</td>
            </tr>
            <tr>
              <td>
                {t("struct.strateg.rand_nu_t")}
                <span className="sg-muted"> ({r.intermediare.nota_nu_t})</span>
              </td>
              <td className="sg-mono">{r.intermediare.nu_t}</td>
            </tr>
            <tr>
              <td>
                {t("struct.strateg.rand_c_s")}
                <Info
                  titlu={t("struct.strateg.c_s_fractiune")}
                  ce={t("struct.strateg.formula_c_s")}
                  deCe={t("struct.strateg.a_g_tare")}
                  parametri={[
                    { simbol: "a_g", nume: t("struct.strateg.acceleratia_terenului_cu"), sursa: t("struct.strateg.harta_zonare_a") },
                    { simbol: "β0", nume: t("struct.strateg.factorul_amplificare"), sursa: t("struct.strateg.p100_1_constant") },
                    { simbol: "q", nume: t("struct.strateg.factorul_comportare_presupus"), sursa: t("struct.strateg.constanta_a_metodei2") },
                    { simbol: "λ", nume: t("struct.strateg.corectia_modala"), sursa: t("struct.strateg.randul_sus") },
                  ]}
                />
              </td>
              <td className="sg-mono">{r.intermediare.c_s}</td>
            </tr>
            <tr>
              <td>
                {t("struct.strateg.rand_v")}
                <Info
                  titlu={t("struct.strateg.v_impingerea_totala")}
                  ce={t("struct.strateg.formula_v_c")}
                  deCe={t("struct.strateg.forta_orizontala_totala")}
                  parametri={[
                    { simbol: "c_s", nume: t("struct.strateg.coeficientul_seismic"), sursa: t("struct.strateg.randul_sus") },
                    { simbol: "A", nume: t("struct.strateg.aria_unui_nivel"), sursa: t("struct.strateg.sursa_pasul_2_media") },
                    { simbol: "n", nume: t("struct.strateg.numarul_niveluri"), sursa: t("struct.strateg.sursa_pasul_2") },
                    { simbol: "g_seism", nume: t("struct.strateg.greutatea_seismica_metru"), sursa: t("struct.strateg.constanta_a_metodei") },
                  ]}
                />
              </td>
              <td className="sg-mono">{r.intermediare.V_kn} kN</td>
            </tr>
            <tr>
              <td>
                {t("struct.strateg.rand_v_rd")}
                <Info
                  titlu={t("struct.strateg.v_rd_duce")}
                  ce={t("struct.strateg.tinta_practica_2000")}
                  deCe={t("struct.strateg.valoare_predimensionare")}
                  parametri={[{ simbol: "v_Rd", nume: t("struct.strateg.capacitatea_practica_for"), sursa: t("struct.strateg.documentul_metoda_consta") }]}
                />
              </td>
              <td className="sg-mono">{r.intermediare.v_Rd_kpa} kPa</td>
            </tr>
            <tr>
              <td>
                {t("struct.strateg.rand_d_top")}
                <Info
                  titlu={t("struct.strateg.d_top_are")}
                  ce={t("struct.strateg.formula_d_top")}
                  deCe={t("struct.strateg.norma_limiteaza_poate")}
                  parametri={[
                    { simbol: "δ_lim", nume: t("struct.strateg.driftul_real_admis", { delta: r.intermediare.delta_lim }),
                      sursa: r.intermediare.nota_delta_lim ?? t("struct.strateg.documentul_metoda_p100") },
                    { simbol: "H", nume: t("struct.strateg.inaltimea_totala"), sursa: t("struct.strateg.randul_sus") },
                    { simbol: "f_conc", nume: t("struct.strateg.concentrarea_driftului_e"), sursa: t("struct.strateg.documentul_metoda") },
                  ]}
                />
              </td>
              <td className="sg-mono">{r.intermediare.D_top_m} m</td>
            </tr>
            <tr>
              <td>
                {t("struct.strateg.rand_i_req")}
                <Info
                  titlu={t("struct.strateg.i_req_cata")}
                  ce={t("struct.strateg.formula_i_req")}
                  deCe={t("struct.strateg.cladirea_consola_vertica")}
                  parametri={[
                    { simbol: "V, H", nume: t("struct.strateg.forta_taietoare_inaltime"), sursa: t("struct.strateg.randurile_sus") },
                    { simbol: "E_c", nume: t("struct.strateg.modulul_elasticitate_bet"), sursa: t("struct.strateg.clasa_date_cladire") },
                    { simbol: "0,5", nume: t("struct.strateg.reducerea_rigiditate_bet"), sursa: "P100-1" },
                    { simbol: "D_top", nume: t("struct.strateg.deplasarea_admisa_varf"), sursa: t("struct.strateg.randul_sus") },
                  ]}
                />
              </td>
              <td className="sg-mono">{r.intermediare.I_req_m4} m⁴</td>
            </tr>
            <tr>
              <td>
                {t("struct.strateg.rand_l_max")}
                <Info
                  titlu={t("struct.strateg.l_max_departe")}
                  ce={t("struct.strateg.formula_l_max")}
                  deCe={t("struct.strateg.grinzile_trebuie_sa")}
                  parametri={[
                    { simbol: t("struct.strateg.sim_h_grinda"), nume: t("struct.strateg.inaltimea_grinda_incape"), sursa: t("struct.strateg.constanta_a_metodei") },
                    { simbol: "12", nume: t("struct.strateg.raportul_practic_deschid"), sursa: t("struct.strateg.regula_predimensionare_d") },
                  ]}
                />
              </td>
              <td className="sg-mono">{r.intermediare.L_max_m} m</td>
            </tr>
            <tr>
              <td>
                {t("struct.strateg.rand_pas")}
                <Info
                  titlu={t("struct.strateg.pasul_dintre_elemente")}
                  ce={t("struct.strateg.formula_pas_min")}
                  deCe={t("struct.strateg.chiar_daca_grinda")}
                  parametri={[
                    { simbol: "L_max", nume: t("struct.strateg.deschiderea_maxima_grind"), sursa: t("struct.strateg.randul_sus") },
                    { simbol: "5 m", nume: t("struct.strateg.pasul_practic_maxim"), sursa: t("struct.strateg.documentul_metoda") },
                  ]}
                />
              </td>
              <td className="sg-mono">{r.intermediare.pas_m} m</td>
            </tr>
            <tr>
              <td>
                {t("struct.strateg.rand_n_elem")}
                <Info
                  titlu={t("struct.strateg.n_elem_cate")}
                  ce={t("struct.strateg.formula_n_elem")}
                  deCe={t("struct.strateg.daca_pui_elemente")}
                  parametri={[
                    { simbol: "A", nume: t("struct.strateg.aria_unui_nivel"), sursa: t("struct.strateg.sursa_pasul_2_media") },
                    { simbol: "pas", nume: t("struct.strateg.distanta_dintre_elemente"), sursa: t("struct.strateg.randul_sus") },
                  ]}
                />
              </td>
              <td className="sg-mono">{r.intermediare.n_elem}</td>
            </tr>
            <tr>
              <td>
                {t("struct.strateg.rand_a_elem")}
                <Info
                  titlu={t("struct.strateg.a_elem_mare")}
                  ce={t("struct.strateg.formula_a_elem")}
                  deCe={t("struct.strateg.regula_alcatuire_capacit")}
                  parametri={[
                    { simbol: t("struct.strateg.sim_h_grinda"), nume: t("struct.strateg.inaltimea_grinzii"), sursa: t("struct.strateg.constanta_a_metodei") },
                    { simbol: "0,09 m²", nume: t("struct.strateg.aria_minima_30"), sursa: t("struct.strateg.regula_constructiva") },
                  ]}
                />
              </td>
              <td className="sg-mono">{r.intermediare.A_elem_m2} m²</td>
            </tr>
          </tbody>
        </table>
        <div className="sg-muted st-ran-at">{t("struct.pas.rulat_la", { cand: fmtDate(state.updated_at) })}</div>
      </div>

      <RegimuriGhid rho={r.rho} guverneaza={r.guverneaza} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Clasa de ductilitate (P100-1/2013 §5.2.1 + tabelul 5.1)
// ---------------------------------------------------------------------------

function Ductilitate({ d }: { d: DuctilitateT }) {
  useT();
  // rândul pereților din tabelul 5.1 are și k_w (§5.2.2.2(4))
  const pereti = !!d.sistem_structural
    && Object.values(d.sistem_structural).includes("pereti");
  return (
    <div className="st-findings">
      <h3>
        {t("struct.strateg.clasa_ductilitate_titlu")}
        <Info
          titlu={t("struct.strateg.clasa_ductilitate_alege")}
          ce={t("struct.strateg.p100_1_2013")}
          deCe={t("struct.strateg.alegerea_compromis_econo")}
          parametri={[
            { simbol: "q", nume: t("struct.strateg.factorul_comportare_redu"), sursa: "P100-1/2013 tab. 5.1: q = q0 · αu/α1" },
            { simbol: "αu/α1", nume: t("struct.strateg.redundanta_suprarezisten"), sursa: t("struct.strateg.sursa_tab51_nota", { nota: d.nota_alfa }) },
            { simbol: "a_g", nume: t("struct.strateg.acceleratia_proiectare_a"), sursa: t("struct.strateg.sursa_pasul_1_zonare") },
          ]}
        />
      </h3>

      <div className="st-cards">
        <div className="st-card st-card-main">
          <div className="st-card-label">{t("struct.strateg.clasa_selectata")}<Marime cheie="clasa_ductilitate" /></div>
          <div className="st-card-value">{d.clasa}</div>
          <div className="st-card-sub">
            {d.detalii.nume} — {d.sursa}
          </div>
        </div>
        <div className="st-card">
          <div className="st-card-label">{t("struct.strateg.factorul_comportare")}<EtichetaNatura cheie="q" /><Marime cheie="q" /></div>
          <div className="st-card-value">q ≤ {d.q_max}</div>
          <div className="st-card-sub">{d.nota_q}</div>
        </div>
      </div>

      <div className="sg-muted" style={{ margin: "8px 0" }}>
        {d.detalii.descriere}
      </div>

      <h4 style={{ margin: "14px 0 6px" }}>{t("struct.strateg.criteriile_ordinea_aplic")}</h4>
      <table className="sg-table">
        <thead>
          <tr><th>{t("struct.ef.c_criteriu")}</th><th>{t("verificari.col_verdict")}</th><th>{t("struct.strateg.referinta")}</th></tr>
        </thead>
        <tbody>
          {d.criterii.map((c, i) => (
            <tr key={i}>
              <td>{c.criteriu}</td>
              <td className="sg-mono" style={{ whiteSpace: "nowrap" }}>{c.verdict}</td>
              <td className="sg-muted">
                {c.explicatie}
                <div style={{ marginTop: 4, fontStyle: "italic" }}>{c.referinta}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* sistemul e decizia Pasului 3 (`sistem_structural`), nu mereu cadre */}
      <h4 style={{ margin: "14px 0 6px" }}>
        {t("struct.strateg.comparatia_claselor", {
          sistem: d.sistem_structural
            ? `X: ${d.sistem_structural.X}, Y: ${d.sistem_structural.Y}` : t("struct.ars.cadre"),
          alfa: d.alfa_u1,
        })}
      </h4>
      <table className="sg-table">
        <thead>
          <tr>
            <th>{t("struct.strateg.col_clasa")}</th>
            <th title={pereti
              ? t("struct.strateg.q_max_pereti", { k_w: d.k_w ?? 1 })
              : "q = q0 · αu/α1 (P100-1 tab. 5.1)"}>q max</th>
            <th title={t("struct.strateg.bugetul_forfecare_re")}>{t("struct.strateg.col_rho_forfecare")}</th>
            <th title={t("struct.strateg.driftul_scade_q")}>ρ drift</th>
            <th>{t("struct.strateg.permisa_aici")}</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(d.optiuni).map(([k, o]) => (
            <tr key={k} style={k === d.clasa ? { fontWeight: 600 } : {}}>
              <td>{k}{k === d.clasa ? t("struct.strateg.sageata_selectata") : ""}</td>
              <td className="sg-mono">{o.q_max}</td>
              <td className="sg-mono">{(o.rho_forfecare * 100).toFixed(2)}%</td>
              <td className="sg-mono">{(o.rho_drift * 100).toFixed(2)}%</td>
              <td className="sg-mono">{o.permisa ? t("struct.det.da") : t("struct.strateg.nepermisa_ag")}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h4 style={{ margin: "14px 0 6px" }}>{t("struct.strateg.ce_implica_clasa", { clasa: d.clasa })}</h4>
      {d.detalii.implicatii.map((x, i) => (
        <div key={i} className="sg-muted" style={{ marginBottom: 4 }}>• {x}</div>
      ))}
    </div>
  );
}
